// Saurellius Knowledge Base — Payroll Articles, Tips & Tricks, Help
export const CATEGORIES = [
  { id: "all", label: "All Articles", icon: "grid" },
  { id: "state-taxes", label: "State Tax Guides", icon: "map" },
  { id: "payroll-basics", label: "Payroll Basics", icon: "book" },
  { id: "tips", label: "Tips & Tricks", icon: "zap" },
  { id: "compliance", label: "Compliance & Law", icon: "shield" },
  { id: "deductions", label: "Deductions & Benefits", icon: "calculator" },
  { id: "help", label: "Help & FAQ", icon: "help" },
];

export const ARTICLES = [
  // ── PAYROLL BASICS ────────────────────────────────────────
  {
    id: "what-is-a-paystub",
    category: "payroll-basics",
    title: "What Is a Pay Stub? Complete Guide",
    summary: "Everything about pay stubs — what they include and why they matter for employees and employers.",
    readTime: "5 min",
    content: `<h2>What Is a Pay Stub?</h2>
<p>A <strong>pay stub</strong> (also called a paycheck stub, wage statement, or earnings statement) is a document accompanying a paycheck that details how an employee's pay was calculated.</p>

<h3>What's Included on a Pay Stub</h3>
<ul>
<li><strong>Gross Pay</strong> — Total earnings before deductions</li>
<li><strong>Net Pay</strong> — Take-home pay after all deductions</li>
<li><strong>Federal Income Tax</strong> — Withheld based on W-4 elections</li>
<li><strong>State Income Tax</strong> — Varies by state (9 states have none)</li>
<li><strong>Social Security (FICA)</strong> — 6.2% up to $168,600 wage base</li>
<li><strong>Medicare</strong> — 1.45% on all wages; additional 0.9% for high earners</li>
<li><strong>Deductions</strong> — Health insurance, 401(k), garnishments</li>
<li><strong>YTD Totals</strong> — Year-to-date cumulative figures</li>
<li><strong>Pay Period</strong> — Start and end dates</li>
<li><strong>Hours Worked</strong> — Regular and overtime (hourly employees)</li>
</ul>

<h3>Why Pay Stubs Matter</h3>
<ol>
<li><strong>Proof of Income</strong> — Required for loans, mortgages, and rental applications</li>
<li><strong>Tax Accuracy</strong> — Verify W-2 figures at year end</li>
<li><strong>Budgeting</strong> — Track earnings and deductions over time</li>
<li><strong>Employment Verification</strong> — Confirm job and salary details</li>
<li><strong>Legal Protection</strong> — Evidence in wage disputes</li>
</ol>

<h3>Are Pay Stubs Required by Law?</h3>
<p>There is <strong>no federal law</strong> requiring employers to provide pay stubs. However, most states have their own requirements. States like California, New York, and Texas require employers to provide detailed wage statements.</p>

<h3>How Long Should You Keep Pay Stubs?</h3>
<p>Keep pay stubs for at least <strong>one year</strong> for tax purposes. After filing your tax return, keep them until you've verified the information matches your W-2. For major financial transactions (mortgage applications), keep at least 2-3 recent pay stubs on hand.</p>`
  },
  {
    id: "gross-vs-net-pay",
    category: "payroll-basics",
    title: "Gross Pay vs. Net Pay: What's the Difference?",
    summary: "Understand the critical difference between your total earnings and your take-home pay.",
    readTime: "4 min",
    content: `<h2>Gross Pay vs. Net Pay</h2>
<p>Understanding the difference between gross and net pay is fundamental to managing your finances.</p>

<h3>Gross Pay</h3>
<p><strong>Gross pay</strong> is your total compensation before any deductions. For hourly workers, it's hours worked × hourly rate. For salaried employees, it's your annual salary divided by pay periods.</p>
<p><em>Example:</em> If you earn $25/hour and work 80 hours in a bi-weekly period, your gross pay is $2,000.</p>

<h3>Net Pay</h3>
<p><strong>Net pay</strong> (take-home pay) is what you actually receive after all deductions are subtracted from gross pay.</p>

<h3>Common Deductions</h3>
<table>
<tr><th>Deduction</th><th>Rate</th><th>Who Pays</th></tr>
<tr><td>Federal Income Tax</td><td>10%–37%</td><td>Employee</td></tr>
<tr><td>Social Security</td><td>6.2%</td><td>Both (12.4% total)</td></tr>
<tr><td>Medicare</td><td>1.45%</td><td>Both (2.9% total)</td></tr>
<tr><td>State Income Tax</td><td>0%–13.3%</td><td>Employee</td></tr>
<tr><td>Health Insurance</td><td>Varies</td><td>Often shared</td></tr>
<tr><td>401(k)/Retirement</td><td>Varies</td><td>Employee (often matched)</td></tr>
</table>

<h3>Quick Formula</h3>
<p><strong>Net Pay = Gross Pay − Federal Tax − State Tax − FICA − Medicare − Other Deductions</strong></p>
<p>Typically, deductions consume 25%–35% of gross pay for most workers.</p>`
  },
  {
    id: "understanding-w4",
    category: "payroll-basics",
    title: "Understanding Your W-4 Form",
    summary: "How to fill out your W-4 correctly to avoid owing taxes or over-withholding.",
    readTime: "6 min",
    content: `<h2>The W-4 Form Explained</h2>
<p>The <strong>Form W-4</strong> (Employee's Withholding Certificate) tells your employer how much federal income tax to withhold from your paycheck.</p>

<h3>Key Sections</h3>
<ul>
<li><strong>Step 1</strong> — Personal information and filing status (Single, Married Filing Jointly, Head of Household)</li>
<li><strong>Step 2</strong> — Multiple jobs or spouse works (use IRS worksheet or estimator)</li>
<li><strong>Step 3</strong> — Claim dependents ($2,000 per qualifying child, $500 per other dependent)</li>
<li><strong>Step 4</strong> — Other adjustments (other income, deductions, extra withholding)</li>
<li><strong>Step 5</strong> — Sign and date</li>
</ul>

<h3>Common Mistakes to Avoid</h3>
<ol>
<li><strong>Not updating after life changes</strong> — Marriage, divorce, new child, new job</li>
<li><strong>Claiming too many allowances</strong> — Results in owing taxes at filing time</li>
<li><strong>Ignoring Step 2</strong> — If both spouses work, you may underwithhold</li>
<li><strong>Not accounting for side income</strong> — Freelance, investment, rental income</li>
</ol>

<h3>When to Update Your W-4</h3>
<p>Update whenever you experience a major life event: new job, marriage, divorce, birth of a child, buying a home, or significant income changes.</p>

<h3>Pro Tip</h3>
<p>Use the <strong>IRS Tax Withholding Estimator</strong> (irs.gov/W4App) to calculate the optimal withholding for your situation.</p>`
  },
  {
    id: "pay-frequency-guide",
    category: "payroll-basics",
    title: "Pay Frequency Guide: Weekly, Bi-Weekly, Semi-Monthly & Monthly",
    summary: "Compare the four most common pay schedules and understand how they affect your paycheck.",
    readTime: "4 min",
    content: `<h2>Pay Frequency Explained</h2>

<h3>The Four Pay Frequencies</h3>
<table>
<tr><th>Frequency</th><th>Pay Periods/Year</th><th>Typical Pay Day</th></tr>
<tr><td><strong>Weekly</strong></td><td>52</td><td>Every Friday</td></tr>
<tr><td><strong>Bi-Weekly</strong></td><td>26</td><td>Every other Friday</td></tr>
<tr><td><strong>Semi-Monthly</strong></td><td>24</td><td>1st and 15th</td></tr>
<tr><td><strong>Monthly</strong></td><td>12</td><td>Last day of month</td></tr>
</table>

<h3>How Pay Frequency Affects Your Check</h3>
<p>If your annual salary is $60,000:</p>
<ul>
<li><strong>Weekly:</strong> $1,153.85 gross per check</li>
<li><strong>Bi-Weekly:</strong> $2,307.69 gross per check</li>
<li><strong>Semi-Monthly:</strong> $2,500.00 gross per check</li>
<li><strong>Monthly:</strong> $5,000.00 gross per check</li>
</ul>

<h3>Which Is Most Common?</h3>
<p><strong>Bi-weekly</strong> is the most popular pay frequency in the United States, used by approximately 43% of employers. Weekly is common in construction and hourly work. Semi-monthly and monthly are common for salaried positions.</p>

<h3>State Requirements</h3>
<p>Many states mandate minimum pay frequencies. For example, California requires at least semi-monthly pay. Check your state's labor laws for specific requirements.</p>`
  },
  {
    id: "overtime-calculations",
    category: "payroll-basics",
    title: "How Overtime Pay Is Calculated",
    summary: "Federal and state overtime rules, how to calculate OT pay, and common exemptions.",
    readTime: "5 min",
    content: `<h2>Overtime Pay Calculations</h2>

<h3>Federal Rules (FLSA)</h3>
<p>The Fair Labor Standards Act requires overtime pay of <strong>1.5× regular rate</strong> for hours worked over <strong>40 in a workweek</strong>.</p>

<h3>Calculation Example</h3>
<p>Employee earns $20/hour and works 48 hours:</p>
<ul>
<li>Regular: 40 hours × $20 = $800</li>
<li>Overtime: 8 hours × $30 (1.5 × $20) = $240</li>
<li><strong>Total Gross: $1,040</strong></li>
</ul>

<h3>State Variations</h3>
<ul>
<li><strong>California:</strong> OT after 8 hours/day AND 40 hours/week. Double time after 12 hours/day</li>
<li><strong>Alaska:</strong> OT after 8 hours/day for employers with 4+ employees</li>
<li><strong>Colorado:</strong> OT after 12 hours/day or 40 hours/week</li>
<li><strong>Nevada:</strong> OT after 8 hours/day if employee earns less than 1.5× minimum wage</li>
</ul>

<h3>Exempt vs. Non-Exempt</h3>
<p><strong>Exempt employees</strong> (typically salaried, earning $43,888+/year, performing executive/administrative/professional duties) are NOT entitled to overtime pay.</p>
<p><strong>Non-exempt employees</strong> MUST receive overtime pay regardless of whether they are paid hourly or salary.</p>`
  },
  {
    id: "understanding-fica",
    category: "payroll-basics",
    title: "FICA Taxes Explained: Social Security & Medicare",
    summary: "How FICA taxes work, current rates, wage bases, and what self-employed individuals need to know.",
    readTime: "5 min",
    content: `<h2>FICA Taxes: Social Security & Medicare</h2>
<p>FICA (Federal Insurance Contributions Act) taxes fund Social Security and Medicare programs.</p>

<h3>Current Rates (2025)</h3>
<table>
<tr><th>Tax</th><th>Employee</th><th>Employer</th><th>Total</th><th>Wage Base</th></tr>
<tr><td>Social Security</td><td>6.2%</td><td>6.2%</td><td>12.4%</td><td>$168,600</td></tr>
<tr><td>Medicare</td><td>1.45%</td><td>1.45%</td><td>2.9%</td><td>No limit</td></tr>
<tr><td>Additional Medicare</td><td>0.9%</td><td>—</td><td>0.9%</td><td>>$200K individual</td></tr>
</table>

<h3>Key Points</h3>
<ul>
<li>Social Security tax stops once you earn above the wage base ($168,600 in 2025)</li>
<li>Medicare has NO wage base limit — all earnings are taxed</li>
<li>Additional Medicare Tax of 0.9% applies to earnings over $200,000 (single) or $250,000 (married)</li>
<li>Self-employed individuals pay BOTH halves (15.3% total) as "self-employment tax"</li>
<li>Self-employed can deduct the employer-equivalent portion (7.65%) on their tax return</li>
</ul>`
  },

  // ── TIPS & TRICKS ─────────────────────────────────────────
  {
    id: "maximize-take-home",
    category: "tips",
    title: "10 Ways to Maximize Your Take-Home Pay",
    summary: "Legal strategies to keep more of your hard-earned money in every paycheck.",
    readTime: "6 min",
    content: `<h2>Maximize Your Take-Home Pay</h2>

<h3>1. Optimize Your W-4</h3>
<p>Use the IRS withholding estimator to ensure you're not over-withholding. Getting a big tax refund means you gave the government an interest-free loan.</p>

<h3>2. Maximize Pre-Tax Contributions</h3>
<p>401(k) contributions reduce taxable income. In 2025, you can contribute up to $23,500 ($31,000 if 50+).</p>

<h3>3. Use an HSA</h3>
<p>Health Savings Accounts offer triple tax benefits: tax-deductible contributions, tax-free growth, and tax-free withdrawals for medical expenses. 2025 limits: $4,300 individual / $8,550 family.</p>

<h3>4. Leverage FSAs</h3>
<p>Flexible Spending Accounts for healthcare ($3,300) and dependent care ($5,000) reduce taxable income.</p>

<h3>5. Commuter Benefits</h3>
<p>Pre-tax transit and parking benefits up to $325/month each (2025).</p>

<h3>6. Review Your Benefits Annually</h3>
<p>During open enrollment, compare health plan costs. A higher-deductible plan with HSA may save more than a low-deductible plan.</p>

<h3>7. Negotiate Your Salary</h3>
<p>A $5,000 raise translates to roughly $150-200 more per paycheck (after taxes).</p>

<h3>8. Consider State Tax Impact</h3>
<p>If you're remote, working from a no-income-tax state (FL, TX, WA, NV, etc.) can save 5-13% of your income.</p>

<h3>9. Avoid Unnecessary Garnishments</h3>
<p>Stay current on child support, student loans, and tax obligations to prevent wage garnishment.</p>

<h3>10. Track and Verify Every Stub</h3>
<p>Errors happen. Review each pay stub for accuracy in hours, rate, and deductions. Report discrepancies immediately.</p>`
  },
  {
    id: "common-paystub-errors",
    category: "tips",
    title: "Common Pay Stub Errors and How to Spot Them",
    summary: "The most frequent payroll mistakes and how to identify and resolve them quickly.",
    readTime: "4 min",
    content: `<h2>Common Pay Stub Errors</h2>

<h3>Top Errors to Watch For</h3>
<ol>
<li><strong>Incorrect Hours</strong> — Verify against your own records. Even 15 minutes per day adds up to $1,000+/year at $20/hr</li>
<li><strong>Wrong Tax Rate</strong> — Especially after moving states or updating W-4</li>
<li><strong>Missing Overtime</strong> — Ensure all hours over 40 (or 8/day in CA) are at 1.5× rate</li>
<li><strong>Incorrect Deductions</strong> — Insurance premiums or retirement contributions that don't match your elections</li>
<li><strong>Classification Error</strong> — Being classified as exempt when you should be non-exempt (affects OT eligibility)</li>
<li><strong>YTD Discrepancies</strong> — Running totals that don't add up from previous stubs</li>
<li><strong>Wrong Pay Rate</strong> — After a raise, verify the new rate appears on the next stub</li>
</ol>

<h3>What to Do If You Find an Error</h3>
<ol>
<li>Document the discrepancy with specific amounts and dates</li>
<li>Notify your HR or payroll department in writing</li>
<li>Keep copies of all related pay stubs</li>
<li>If unresolved, file a complaint with your state labor department</li>
<li>For federal violations, contact the Department of Labor's Wage and Hour Division</li>
</ol>`
  },
  {
    id: "reading-your-paystub",
    category: "tips",
    title: "How to Read Your Pay Stub Like a Pro",
    summary: "A line-by-line breakdown of every section on your pay stub and what each number means.",
    readTime: "5 min",
    content: `<h2>Reading Your Pay Stub</h2>

<h3>Header Section</h3>
<ul>
<li><strong>Employer Info</strong> — Company name, address, EIN (Employer Identification Number)</li>
<li><strong>Employee Info</strong> — Your name, address, SSN (usually masked as XXX-XX-1234)</li>
<li><strong>Pay Period</strong> — Start date and end date of the period covered</li>
<li><strong>Pay Date</strong> — When you receive the payment</li>
</ul>

<h3>Earnings Section</h3>
<ul>
<li><strong>Regular</strong> — Base pay (hours × rate or salary portion)</li>
<li><strong>Overtime</strong> — Hours beyond 40/week at premium rate</li>
<li><strong>Holiday/PTO</strong> — Paid time off if used during this period</li>
<li><strong>Bonus/Commission</strong> — Any supplemental wages</li>
</ul>

<h3>Deductions Section</h3>
<ul>
<li><strong>Federal Withholding</strong> — Based on W-4, filing status, and income level</li>
<li><strong>State Withholding</strong> — Based on state tax tables and your elections</li>
<li><strong>Social Security</strong> — 6.2% of gross (up to wage base)</li>
<li><strong>Medicare</strong> — 1.45% of all gross wages</li>
<li><strong>Pre-Tax Deductions</strong> — 401(k), HSA, FSA, health insurance premiums</li>
<li><strong>Post-Tax Deductions</strong> — Roth 401(k), garnishments, union dues</li>
</ul>

<h3>Summary Section</h3>
<ul>
<li><strong>Current Period</strong> — Earnings and deductions for this check</li>
<li><strong>YTD (Year-to-Date)</strong> — Cumulative totals since January 1</li>
<li><strong>Net Pay</strong> — Your actual take-home amount</li>
</ul>`
  },
  {
    id: "self-employed-paystubs",
    category: "tips",
    title: "Pay Stubs for Self-Employed & Freelancers",
    summary: "How to create professional pay stubs when you're your own boss.",
    readTime: "4 min",
    content: `<h2>Self-Employed Pay Stubs</h2>
<p>Self-employed individuals, freelancers, and independent contractors don't receive traditional pay stubs from employers. But you still may need proof of income.</p>

<h3>When You Need Pay Stubs</h3>
<ul>
<li>Applying for a mortgage or loan</li>
<li>Renting an apartment</li>
<li>Applying for credit cards</li>
<li>Proving income for government programs</li>
<li>Business record-keeping</li>
</ul>

<h3>What to Include</h3>
<ul>
<li>Your business name and address</li>
<li>Pay period dates</li>
<li>Gross income for the period</li>
<li>Estimated tax withholding (federal + state + self-employment tax)</li>
<li>Business expenses and deductions</li>
<li>Net pay</li>
</ul>

<h3>Self-Employment Tax</h3>
<p>Self-employed individuals pay <strong>15.3%</strong> self-employment tax (12.4% Social Security + 2.9% Medicare) on net earnings. This is because you're paying both the employee AND employer portions.</p>
<p>You can deduct the employer-equivalent portion (7.65%) when filing your tax return.</p>

<h3>Pro Tip</h3>
<p>Use Saurellius to generate professional pay stubs that are accepted by lenders, landlords, and other institutions requiring proof of income.</p>`
  },

  // ── COMPLIANCE & LAW ──────────────────────────────────────
  {
    id: "flsa-overview",
    category: "compliance",
    title: "FLSA: Fair Labor Standards Act Overview",
    summary: "Federal wage and hour laws every employer and employee should know.",
    readTime: "6 min",
    content: `<h2>Fair Labor Standards Act (FLSA)</h2>
<p>The FLSA is the primary federal law governing wages, hours, and employment standards in the United States.</p>

<h3>Key Provisions</h3>
<ul>
<li><strong>Minimum Wage:</strong> Federal minimum is $7.25/hour (many states set higher rates)</li>
<li><strong>Overtime:</strong> 1.5× regular rate for hours over 40/week</li>
<li><strong>Child Labor:</strong> Restrictions on employment of minors</li>
<li><strong>Recordkeeping:</strong> Employers must maintain accurate payroll records</li>
</ul>

<h3>Exempt vs. Non-Exempt</h3>
<p>To be <strong>exempt</strong> from overtime, an employee must meet ALL three tests:</p>
<ol>
<li><strong>Salary Basis Test:</strong> Paid a predetermined, fixed salary</li>
<li><strong>Salary Level Test:</strong> Earn at least $43,888/year ($844/week)</li>
<li><strong>Duties Test:</strong> Perform executive, administrative, professional, computer, or outside sales duties</li>
</ol>

<h3>Required Payroll Records</h3>
<p>Employers must keep for at least 3 years:</p>
<ul>
<li>Employee's full name and SSN</li>
<li>Address including zip code</li>
<li>Birth date (if under 19)</li>
<li>Sex and occupation</li>
<li>Time and day of week workweek begins</li>
<li>Hours worked each day and total each week</li>
<li>Basis of pay (hourly rate, salary, etc.)</li>
<li>Regular hourly pay rate</li>
<li>Total daily or weekly straight-time earnings</li>
<li>Total overtime for the workweek</li>
<li>All additions to or deductions from wages</li>
<li>Total wages paid each pay period</li>
<li>Date of payment and period covered</li>
</ul>`
  },
  {
    id: "paystub-laws-by-state",
    category: "compliance",
    title: "Pay Stub Laws by State: What's Required?",
    summary: "Which states require pay stubs, what they must contain, and penalties for non-compliance.",
    readTime: "5 min",
    content: `<h2>State Pay Stub Requirements</h2>

<h3>Three Categories of States</h3>

<h4>1. Access States (must provide)</h4>
<p>Employers MUST provide written or printed pay stubs: <strong>California, Colorado, Connecticut, Iowa, Maine, Massachusetts, Michigan, Minnesota, New Hampshire, New York, North Carolina, Ohio, Oregon, Pennsylvania, Rhode Island, Texas, Vermont, Virginia, Washington, Wisconsin</strong> and others.</p>

<h4>2. Access/Print States</h4>
<p>Must provide stubs but electronic access is acceptable: <strong>Alaska, Arizona, Illinois, Indiana, Maryland, Missouri, Montana, Nebraska, New Jersey, North Dakota, Oklahoma, Utah</strong> and others.</p>

<h4>3. No Requirement States</h4>
<p>Alabama, Arkansas, Florida, Georgia, Louisiana, Mississippi, Ohio, South Dakota, Tennessee — have no state law requiring pay stubs.</p>

<h3>What Must Be Included (Common Requirements)</h3>
<ul>
<li>Gross wages earned</li>
<li>Net wages paid</li>
<li>All deductions with itemization</li>
<li>Hours worked (hourly employees)</li>
<li>Pay period dates</li>
<li>Employee name or identifier</li>
<li>Employer name and address</li>
</ul>

<h3>California (Strictest Requirements)</h3>
<p>Under Labor Code §226, California pay stubs must include: gross wages, total hours worked, piece rates, all deductions, net wages, pay period dates, employee name + last 4 SSN, employer legal name + address, and all applicable hourly rates with corresponding hours.</p>

<h3>Penalties</h3>
<p>Non-compliance can result in fines of $50-$250 per violation, plus employee lawsuits and back-pay awards.</p>`
  },
  {
    id: "worker-classification",
    category: "compliance",
    title: "Employee vs. Independent Contractor: Classification Guide",
    summary: "How to properly classify workers and the tax implications of each classification.",
    readTime: "5 min",
    content: `<h2>Worker Classification</h2>
<p>Proper worker classification is critical — misclassification can result in severe penalties.</p>

<h3>IRS Three-Factor Test</h3>
<ol>
<li><strong>Behavioral Control:</strong> Does the company control how, when, and where the worker performs?</li>
<li><strong>Financial Control:</strong> Does the company control the business aspects (expenses, tools, profit opportunity)?</li>
<li><strong>Relationship Type:</strong> Written contracts, benefits, permanency of relationship?</li>
</ol>

<h3>Key Differences</h3>
<table>
<tr><th>Factor</th><th>Employee</th><th>Independent Contractor</th></tr>
<tr><td>Taxes Withheld</td><td>Yes (W-2)</td><td>No (1099)</td></tr>
<tr><td>FICA</td><td>Split 50/50</td><td>Worker pays 100%</td></tr>
<tr><td>Benefits</td><td>Often eligible</td><td>Not eligible</td></tr>
<tr><td>Tools/Equipment</td><td>Employer provides</td><td>Worker provides</td></tr>
<tr><td>Schedule</td><td>Set by employer</td><td>Set by worker</td></tr>
<tr><td>Overtime</td><td>FLSA eligible</td><td>Not applicable</td></tr>
</table>

<h3>Misclassification Penalties</h3>
<ul>
<li>Back taxes: 100% of employee share of FICA</li>
<li>Federal penalty: Up to $50 per unfiled W-2</li>
<li>IRS penalties: 1.5% of wages + 20% of employee's FICA + 100% of employer's FICA</li>
<li>State penalties vary — California can impose $5,000-$25,000 per violation</li>
</ul>`
  },

  // ── DEDUCTIONS & BENEFITS ─────────────────────────────────
  {
    id: "pretax-vs-posttax",
    category: "deductions",
    title: "Pre-Tax vs. Post-Tax Deductions Explained",
    summary: "Understand which deductions reduce your taxable income and which don't.",
    readTime: "4 min",
    content: `<h2>Pre-Tax vs. Post-Tax Deductions</h2>

<h3>Pre-Tax Deductions</h3>
<p>Reduce your taxable income BEFORE taxes are calculated. This means you pay less in income tax and FICA taxes.</p>
<ul>
<li><strong>Traditional 401(k)</strong> — Up to $23,500/year (2025)</li>
<li><strong>Health Insurance Premiums</strong> — Under Section 125 cafeteria plan</li>
<li><strong>HSA Contributions</strong> — $4,300 individual / $8,550 family (2025)</li>
<li><strong>FSA Contributions</strong> — $3,300 healthcare / $5,000 dependent care</li>
<li><strong>Commuter Benefits</strong> — $325/month transit + $325/month parking</li>
</ul>

<h3>Post-Tax Deductions</h3>
<p>Subtracted AFTER taxes are calculated. No immediate tax savings but may have other benefits.</p>
<ul>
<li><strong>Roth 401(k)</strong> — Contributions are post-tax but withdrawals are tax-free in retirement</li>
<li><strong>Life Insurance</strong> — Premiums over $50,000 group term coverage</li>
<li><strong>Disability Insurance</strong> — Some employer plans</li>
<li><strong>Wage Garnishments</strong> — Court-ordered deductions</li>
<li><strong>Union Dues</strong></li>
<li><strong>Charitable Contributions</strong> — Payroll deductions for charity</li>
</ul>

<h3>Impact Example</h3>
<p>On a $5,000 gross paycheck, a $500 pre-tax 401(k) contribution saves you approximately $125-$175 in combined taxes (depending on bracket), making the effective cost of saving $500 only about $325-$375.</p>`
  },
  {
    id: "401k-guide",
    category: "deductions",
    title: "401(k) Guide: Maximize Your Retirement Savings",
    summary: "Everything about 401(k) plans — contribution limits, employer matching, vesting, and strategies.",
    readTime: "5 min",
    content: `<h2>401(k) Retirement Plans</h2>

<h3>2025 Contribution Limits</h3>
<ul>
<li><strong>Employee:</strong> $23,500 per year</li>
<li><strong>Catch-Up (age 50+):</strong> Additional $7,500 (total $31,000)</li>
<li><strong>Total (employee + employer):</strong> $70,000</li>
</ul>

<h3>Traditional vs. Roth 401(k)</h3>
<table>
<tr><th>Feature</th><th>Traditional</th><th>Roth</th></tr>
<tr><td>Tax on Contributions</td><td>Pre-tax (reduces taxable income now)</td><td>Post-tax (no current tax break)</td></tr>
<tr><td>Tax on Withdrawals</td><td>Taxed as ordinary income</td><td>Tax-free</td></tr>
<tr><td>Best If</td><td>You expect lower tax rate in retirement</td><td>You expect higher tax rate in retirement</td></tr>
</table>

<h3>Employer Match</h3>
<p>Many employers match a percentage of your contributions. Common formulas:</p>
<ul>
<li>100% match on first 3% of salary</li>
<li>50% match on first 6% of salary</li>
<li>Dollar-for-dollar up to 4% of salary</li>
</ul>
<p><strong>Always contribute at least enough to get the full employer match — it's free money.</strong></p>

<h3>Vesting</h3>
<p>Your contributions are always 100% yours. Employer contributions may have a vesting schedule (typically 3-6 years). Common schedules:</p>
<ul>
<li><strong>Cliff vesting:</strong> 0% until year 3, then 100%</li>
<li><strong>Graded vesting:</strong> 20% per year from year 2-6</li>
</ul>`
  },
  {
    id: "health-insurance-deductions",
    category: "deductions",
    title: "Health Insurance & Payroll: How Premiums Affect Your Pay",
    summary: "How employer-sponsored health insurance premiums are deducted and the tax advantages.",
    readTime: "4 min",
    content: `<h2>Health Insurance & Payroll</h2>

<h3>Section 125 Cafeteria Plan</h3>
<p>Most employers use a Section 125 plan, which allows health insurance premiums to be deducted <strong>pre-tax</strong>. This means:</p>
<ul>
<li>Your taxable income is reduced</li>
<li>You pay less federal income tax</li>
<li>You pay less Social Security and Medicare tax</li>
<li>Your employer also saves on FICA taxes</li>
</ul>

<h3>Average Premiums (2025)</h3>
<table>
<tr><th>Coverage</th><th>Total Premium</th><th>Employee Share</th><th>Employer Share</th></tr>
<tr><td>Individual</td><td>$8,951/year</td><td>$1,436/year</td><td>$7,515/year</td></tr>
<tr><td>Family</td><td>$25,572/year</td><td>$6,575/year</td><td>$18,997/year</td></tr>
</table>

<h3>Types of Plans</h3>
<ul>
<li><strong>HMO</strong> — Network-restricted, lower premiums, need referrals</li>
<li><strong>PPO</strong> — Flexible network, higher premiums, no referrals needed</li>
<li><strong>HDHP</strong> — High deductible, lowest premiums, HSA-eligible</li>
<li><strong>EPO</strong> — Network-only (like HMO) but no referrals needed</li>
</ul>

<h3>HSA Strategy</h3>
<p>If you choose an HDHP, pair it with an HSA for triple tax advantages: tax-deductible contributions, tax-free growth, and tax-free withdrawals for medical expenses. After age 65, HSA withdrawals for any purpose are taxed like a traditional IRA (no penalty).</p>`
  },

  // ── HELP & FAQ ────────────────────────────────────────────
  {
    id: "how-to-create-paystub",
    category: "help",
    title: "How to Create a Pay Stub with Saurellius",
    summary: "Step-by-step guide to generating professional, accurate pay stubs using our platform.",
    readTime: "3 min",
    content: `<h2>Creating a Pay Stub with Saurellius</h2>

<h3>Step 1: Company Information</h3>
<p>Enter your company details: name, address, city, state, ZIP code, phone number, and EIN (Employer Identification Number).</p>

<h3>Step 2: Employee Information</h3>
<p>Enter the employee's details: full name, address, SSN (securely masked), employment status (hourly or salaried), and filing status.</p>

<h3>Step 3: Pay Details</h3>
<ul>
<li>Select pay frequency (Weekly, Bi-Weekly, Semi-Monthly, Monthly)</li>
<li>Enter hourly rate or annual salary</li>
<li>Choose pay period dates</li>
<li>Add any additional earnings (bonus, commission, tips)</li>
<li>Add deductions (health insurance, 401k, garnishments)</li>
</ul>

<h3>Step 4: Review & Generate</h3>
<p>Preview your pay stub, choose a professional template, and complete payment. Your pay stubs are:</p>
<ul>
<li>Generated as secure PDF documents</li>
<li>Emailed to the address you specify</li>
<li>Available for immediate download</li>
<li>Stored in your dashboard for future access</li>
</ul>

<h3>Pro Tips</h3>
<ul>
<li>Double-check all calculations before generating</li>
<li>Use consistent formatting across all pay periods</li>
<li>Keep your account active to access all your generated stubs</li>
</ul>`
  },
  {
    id: "paystub-for-apartment",
    category: "help",
    title: "Using Pay Stubs for Apartment Applications",
    summary: "What landlords look for in pay stubs and how to prepare your income documentation.",
    readTime: "4 min",
    content: `<h2>Pay Stubs for Apartment Applications</h2>

<h3>What Landlords Look For</h3>
<ul>
<li><strong>Income Level:</strong> Most landlords require income of 2.5x–3x monthly rent</li>
<li><strong>Employment Stability:</strong> Consistent pay over multiple periods</li>
<li><strong>Employer Verification:</strong> Company name and contact information</li>
<li><strong>YTD Earnings:</strong> To verify annual income claims</li>
</ul>

<h3>How Many Pay Stubs Do You Need?</h3>
<p>Most landlords request <strong>2-3 recent consecutive pay stubs</strong>. Some may ask for up to 6 months of stubs for self-employed applicants.</p>

<h3>What Makes a Pay Stub Acceptable</h3>
<ul>
<li>Professional formatting with clear layout</li>
<li>Complete employer information (name, address, EIN)</li>
<li>Employee name matching your ID</li>
<li>Detailed breakdown of earnings and deductions</li>
<li>Consistent pay amounts across periods</li>
<li>Current dates (within last 30-60 days)</li>
</ul>

<h3>Additional Documentation</h3>
<p>Landlords may also request: bank statements, tax returns, employment verification letter, or a co-signer if income doesn't meet requirements.</p>`
  },
  {
    id: "paystub-for-loan",
    category: "help",
    title: "Pay Stubs for Mortgage & Loan Applications",
    summary: "How lenders use pay stubs to verify income and what you need to qualify.",
    readTime: "4 min",
    content: `<h2>Pay Stubs for Loans</h2>

<h3>Mortgage Applications</h3>
<p>Lenders typically require:</p>
<ul>
<li><strong>30 days of recent pay stubs</strong> from all income sources</li>
<li><strong>2 years of W-2s</strong> to show income history</li>
<li><strong>2 years of tax returns</strong> for self-employed applicants</li>
<li>Pay stubs showing YTD earnings consistent with stated income</li>
</ul>

<h3>What Lenders Verify</h3>
<ul>
<li>Gross monthly income (for DTI ratio calculation)</li>
<li>Employment duration and stability</li>
<li>Overtime and bonus income (usually averaged over 2 years)</li>
<li>Deductions that may affect qualifying income</li>
</ul>

<h3>Debt-to-Income (DTI) Ratio</h3>
<p>Lenders calculate DTI using your gross monthly income from pay stubs:</p>
<ul>
<li><strong>Front-End DTI:</strong> Housing costs ÷ Gross Income (max ~28%)</li>
<li><strong>Back-End DTI:</strong> All debt payments ÷ Gross Income (max ~36-43%)</li>
</ul>

<h3>Auto Loans & Personal Loans</h3>
<p>Usually require 1-2 recent pay stubs. Some lenders accept bank statements showing regular deposits as an alternative.</p>`
  },
  {
    id: "understanding-tax-withholding",
    category: "help",
    title: "Why Is So Much Tax Taken From My Paycheck?",
    summary: "Breaking down exactly where your money goes and how to adjust your withholding.",
    readTime: "5 min",
    content: `<h2>Understanding Tax Withholding</h2>
<p>It's common to be surprised by the amount deducted from your paycheck. Here's where it all goes.</p>

<h3>Breakdown for a $5,000/month Gross Pay (Single Filer)</h3>
<table>
<tr><th>Deduction</th><th>Amount</th><th>Percentage</th></tr>
<tr><td>Federal Income Tax</td><td>~$560</td><td>11.2%</td></tr>
<tr><td>Social Security</td><td>$310</td><td>6.2%</td></tr>
<tr><td>Medicare</td><td>$72.50</td><td>1.45%</td></tr>
<tr><td>State Income Tax (avg)</td><td>~$220</td><td>~4.4%</td></tr>
<tr><td>Health Insurance</td><td>~$120</td><td>~2.4%</td></tr>
<tr><td>401(k) @ 6%</td><td>$300</td><td>6%</td></tr>
<tr><td><strong>Total Deductions</strong></td><td><strong>~$1,582</strong></td><td><strong>~31.6%</strong></td></tr>
<tr><td><strong>Net Pay</strong></td><td><strong>~$3,418</strong></td><td><strong>~68.4%</strong></td></tr>
</table>

<h3>Taxes Are Not Lost Money</h3>
<ul>
<li><strong>Social Security</strong> — Funds your future retirement benefits</li>
<li><strong>Medicare</strong> — Provides health coverage at age 65+</li>
<li><strong>Federal/State Tax</strong> — Funds infrastructure, defense, education, safety net programs</li>
<li><strong>401(k)</strong> — Your own retirement savings (not a tax!)</li>
</ul>

<h3>How to Reduce Withholding (Legally)</h3>
<ol>
<li>Increase pre-tax retirement contributions</li>
<li>Open and contribute to an HSA</li>
<li>Adjust W-4 if you typically get large refunds</li>
<li>Claim all eligible deductions on W-4 Step 4(b)</li>
</ol>`
  },
  {
    id: "year-end-paystub-checklist",
    category: "help",
    title: "Year-End Pay Stub Checklist",
    summary: "Essential items to verify on your final pay stub before tax season.",
    readTime: "3 min",
    content: `<h2>Year-End Pay Stub Checklist</h2>
<p>Your last pay stub of the year should closely match your W-2. Use this checklist:</p>

<h3>Verify These Items</h3>
<ul>
<li>☑ <strong>Total Gross Wages YTD</strong> — Should match W-2 Box 1 (approximately)</li>
<li>☑ <strong>Federal Tax Withheld YTD</strong> — Must match W-2 Box 2</li>
<li>☑ <strong>Social Security Wages</strong> — W-2 Box 3 (max $168,600 in 2025)</li>
<li>☑ <strong>Social Security Tax</strong> — W-2 Box 4</li>
<li>☑ <strong>Medicare Wages</strong> — W-2 Box 5 (no limit)</li>
<li>☑ <strong>Medicare Tax</strong> — W-2 Box 6</li>
<li>☑ <strong>State Wages</strong> — W-2 Box 16</li>
<li>☑ <strong>State Tax Withheld</strong> — W-2 Box 17</li>
<li>☑ <strong>401(k) Contributions</strong> — W-2 Box 12 Code D</li>
<li>☑ <strong>Health Insurance</strong> — W-2 Box 12 Code DD (informational)</li>
</ul>

<h3>Common Discrepancies</h3>
<ul>
<li>W-2 Box 1 may differ from gross wages due to pre-tax deductions</li>
<li>Pre-tax 401(k) reduces Box 1 but NOT Social Security/Medicare wages</li>
<li>HSA contributions may reduce Box 1 but show in Box 12 Code W</li>
</ul>

<h3>What to Do If Numbers Don't Match</h3>
<p>Contact payroll immediately. W-2s must be issued by January 31. If an error exists, request a corrected W-2c before filing your tax return.</p>`
  }
];

export default ARTICLES;
